import { Button } from "react-bootstrap"
import useUserRefs from "../../Hooks/UseUsersQuery/UseUserRefs"
import { useAddResourceAccessMutation, useDeleteResourceAccessMutation, useGetResourceAccessListQuery } from "../../services/bClientApi"
import { useState } from "react"
import canAccess from "../../services/accessService/accessService"
import SelectUserModal from "../Modals/SelectUserModal/selectUserModal"
import { accountSelector } from "../../redux/account/accountSlice"
import { useSelector } from "react-redux"
import { UserType } from "../../Types/userTypes"
import { useAppDispatch } from "../../redux/store"
import { showError, showSuccess } from "../../redux/counter/counterSlice"

export type ResourceType = 'property' | 'building'

export default function GenericResourceAccess(props: {
    resourceType: ResourceType
    resourceId?: string
}) {

    const { _id } = useSelector(accountSelector)
    const dispatch = useAppDispatch()

    const { resourceType, resourceId } = props

    const userRef = useUserRefs()

    const [selectUserModalActive, setSelectUserModalActive] = useState(false)
    const [editMode, setEditMode] = useState(false)

    const [
        addNewResourceAccess,
        {
            isLoading: isAdding,
        }
    ] = useAddResourceAccessMutation()
    const [
        deleteResourceAccess,
        {
            isLoading: isDeleting,
        }
    ] = useDeleteResourceAccessMutation()

    const {
        data: resourceAccess,
    } = useGetResourceAccessListQuery({
        resourceAccessQuery: {
            resourceType: resourceType,
            resourceId: resourceId,
        },
        pagination: {
            page: 1,
            limit: 150,
            all: true,
        }
    }, {
        skip: !resourceId,
    })

    const resourceAccessList = resourceAccess?.docs || []
    
    // Get list of user IDs that already have access for filtering
    const existingUserIds = resourceAccessList.map((ra: any) => ra.userId)

    const sortedResourceAccessList = [...resourceAccessList].sort((a: any, b: any) => {
        const userA = userRef[a.userId]
        const userB = userRef[b.userId]
        if (userA && userB) {
            return userA.firstName.localeCompare(userB.firstName)
        }
        return 0
    })

    const resourceLabel = resourceType.charAt(0).toUpperCase() + resourceType.slice(1)

    const handleUserSelect = async (userData: UserType | UserType[]) => {
        const users = Array.isArray(userData) ? userData : [userData]
        
        let successCount = 0
        let failCount = 0
        
        for (const user of users) {
            // Skip if user already has access
            const isResourceAccessFound = resourceAccessList.some((resourceAccess: any) => {
                return resourceAccess.userId === user._id
            })
            if (isResourceAccessFound) {
                continue
            }
            
            const newResourceAccessInfo = {
                resourceType: resourceType,
                resourceId: resourceId,
                userId: user._id,
                role: user.role ? user.role : 'casual',
            }
            
            try {
                await addNewResourceAccess(newResourceAccessInfo).unwrap()
                successCount++
            } catch (err) {
                console.error('Failed to add user access:', err)
                failCount++
            }
        }
        
        if (successCount > 0) {
            dispatch(showSuccess(`${successCount} user${successCount === 1 ? '' : 's'} added to ${resourceType}`))
        }
        if (failCount > 0) {
            dispatch(showError(`Failed to add ${failCount} user${failCount === 1 ? '' : 's'} to ${resourceType}`))
        }
        
        setSelectUserModalActive(false)
    }

    return (
        <>
            {resourceAccessList && resourceAccessList.length > 0 && (
                <div className="resource-access-list">
                    <h4>{resourceLabel} Access List</h4>

                    <Button
                        className="my-2"
                        size="sm"
                        variant={editMode ? 'danger' : 'primary'}
                        onClick={() => {
                            setEditMode(!editMode)
                        }}
                    >
                        {editMode ? 'Stop Editing' : `Edit ${resourceLabel} Access List`}
                    </Button>

                    {sortedResourceAccessList.map((resourceAccessInfo: any) => {
                        const userId = resourceAccessInfo.userId
                        const userInfo = userRef[userId]
                        return (
                            <div key={userId} className="resource-access-item mb-2 p-2 border border-1 rounded d-flex gap-3 align-items-center">
                                {editMode && canAccess('user', 'authorizeProperty') && (
                                    <Button
                                        size="sm"
                                        disabled={isDeleting || userId === _id}
                                        variant="danger"
                                        onClick={async () => {
                                            await deleteResourceAccess(resourceAccessInfo._id).unwrap()
                                        }}
                                    >
                                        X
                                    </Button>
                                )}
                                <div className="d-flex gap-2 align-items-center flex-wrap">
                                    <div className="fw-bold">
                                        {userInfo?.firstName} {userInfo?.lastName}
                                    </div>
                                    <div>
                                        <span className="small">
                                            {userInfo?.role}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="small fst-italic" style={{ opacity: .5 }}>({userInfo?.email})</span>
                                    </div>
                                </div>

                            </div>
                        )
                    })}

                    {editMode && canAccess('user', 'authorizeProperty') && (
                        <Button
                            className="mb-2"
                            size="sm"
                            disabled={isAdding}
                            variant="success"
                            onClick={() => {
                                setSelectUserModalActive(true)
                            }}
                        >
                            + Add User
                        </Button>
                    )}
                </div>
            )}
            
            {/* Show add button even when list is empty */}
            {(!resourceAccessList || resourceAccessList.length === 0) && canAccess('user', 'authorizeProperty') && (
                <div className="resource-access-list">
                    <h2>{resourceLabel} Access List</h2>
                    <p className="text-muted">No users have access to this {resourceType}.</p>
                    <Button
                        className="mb-2"
                        size="sm"
                        disabled={isAdding}
                        variant="success"
                        onClick={() => {
                            setSelectUserModalActive(true)
                        }}
                    >
                        + Add User
                    </Button>
                </div>
            )}
            
            {selectUserModalActive && (
                <SelectUserModal
                    title={`Select User`}
                    message={`Select users to add to the ${resourceType}`}
                    active={selectUserModalActive}
                    onClose={() => setSelectUserModalActive(false)}
                    onSelect={handleUserSelect}
                    multiSelect={true}
                    filterFn={(user: UserType) => !existingUserIds.includes(user._id)}
                />
            )}
        </>
    )
}
